/**
 * Schemas for the Rails REST API consumed by the games-web app.
 *
 * The Rails backend lives in the CommunityOS repo under
 * `community_os/v1/games/*`. These Zod schemas are the contract; both ends
 * generate types from them.
 *
 * Conventions:
 * - Times are ISO-8601 strings.
 * - IDs the client generates are uuid (idempotency).
 * - Numeric IDs from Rails are bigint-shaped strings to avoid JS number range.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
export const Iso8601 = z.string().datetime();
export const Uuid = z.string().uuid();
export const RailsId = z.string().regex(/^\d+$/);
export const CognitiveDomain = z.enum([
    'processing_speed',
    'working_memory',
    'episodic_memory',
    'attention',
    'inhibition',
    'executive',
    'visuospatial',
    'language',
    'reasoning',
    'navigation',
]);
export const GameRuntime = z.enum(['dom', 'phaser', 'three']);
export const GameFamily = z.enum([
    'word',
    'trivia',
    'memory',
    'tile',
    'arcade',
    'spatial',
    'communal',
    'reasoning',
]);
// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------
export const GameDefinition = z.object({
    id: RailsId,
    slug: z.string(),
    title: z.string(),
    description: z.string().default(''),
    family: GameFamily,
    runtime: GameRuntime,
    cognitiveDomains: z.array(CognitiveDomain).default([]),
    surfaces: z.array(z.enum(['resident-app', 'signage', 'tablet'])).default([]),
    inputModes: z.array(z.enum(['touch', 'pointer', 'dpad', 'companion'])).default([]),
    minDifficulty: z.number().int().min(0).default(0),
    maxDifficulty: z.number().int().min(0).default(10),
    isActive: z.boolean().default(true),
    /** Asset manifest URL (versioned) for offline pre-caching. */
    assetManifestUrl: z.string().url().optional(),
    /** Free-form, per-game configuration. */
    metadata: z.record(z.string(), z.unknown()).default({}),
});
// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------
export const SessionStatus = z.enum(['active', 'completed', 'abandoned', 'errored']);
export const StartSessionRequest = z.object({
    publicId: Uuid,
    gameSlug: z.string(),
    surface: z.enum(['resident-app', 'signage', 'tablet', 'standalone']),
    inputMode: z.enum(['touch', 'pointer', 'dpad', 'companion']),
    deviceKind: z.enum(['phone', 'tablet', 'web', 'tv']),
    clientAppVersion: z.string(),
});
export const StartSessionResponse = z.object({
    publicId: Uuid,
    difficultyLevel: z.number().int().nonnegative(),
    startedAt: Iso8601,
});
export const EndSessionRequest = z.object({
    durationMs: z.number().int().nonnegative(),
    finalScore: z.number().int().nonnegative(),
    accuracy: z.number().min(0).max(1),
    completed: z.boolean(),
    status: SessionStatus.default('completed'),
});
export const PlaySessionView = z.object({
    publicId: Uuid,
    gameSlug: z.string(),
    startedAt: Iso8601,
    endedAt: Iso8601.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    status: SessionStatus,
    finalScore: z.number().int().nonnegative().default(0),
    accuracy: z.number().min(0).max(1).default(0),
    difficultyLevel: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Events (the append-only telemetry stream)
// ---------------------------------------------------------------------------
/** Standard event types — games may add their own under namespaced strings. */
export const StandardEventType = z.enum([
    'game_start',
    'round_start',
    'prompt_shown',
    'move',
    'correct',
    'incorrect',
    'hint_used',
    'pause',
    'resume',
    'level_up',
    'level_down',
    'round_end',
    'game_end',
]);
export const GameEvent = z.object({
    sessionPublicId: Uuid,
    seq: z.number().int().nonnegative(),
    /** Client-side timestamp; the server also records `synced_at`. */
    occurredAt: Iso8601,
    /** Ms since session start — clock-skew-proof ordering signal. */
    elapsedMs: z.number().int().nonnegative(),
    eventType: z.union([StandardEventType, z.string()]),
    /** Set on response events (move / correct / incorrect). Null otherwise. */
    reactionTimeMs: z.number().int().nonnegative().nullable().default(null),
    payload: z.record(z.string(), z.unknown()).default({}),
});
/** The batched-upload endpoint payload. */
export const SyncRequest = z.object({
    sessions: z
        .array(z.object({
        publicId: Uuid,
        gameSlug: z.string(),
        startedAt: Iso8601,
        endedAt: Iso8601.optional(),
        end: EndSessionRequest.optional(),
    }))
        .default([]),
    events: z.array(GameEvent).default([]),
});
export const SyncResponse = z.object({
    acceptedSessions: z.number().int().nonnegative(),
    acceptedEvents: z.number().int().nonnegative(),
    rejectedEvents: z
        .array(z.object({ sessionPublicId: Uuid, seq: z.number().int(), reason: z.string() }))
        .default([]),
});
// ---------------------------------------------------------------------------
// Leaderboards
// ---------------------------------------------------------------------------
export const LeaderboardBoard = z.enum([
    'personal_best',
    'most_improved',
    'team_total',
    'handicapped',
]);
export const LeaderboardScope = z.enum(['global', 'corporate', 'building', 'team']);
export const LeaderboardPeriod = z.enum(['daily', 'weekly', 'monthly', 'all_time']);
export const LeaderboardEntry = z.object({
    rank: z.number().int().positive(),
    residentDisplayName: z.string(),
    residentId: RailsId.optional(),
    score: z.number(),
    delta: z.number().optional(),
});
export const LeaderboardResponse = z.object({
    gameSlug: z.string(),
    board: LeaderboardBoard,
    scope: LeaderboardScope,
    period: LeaderboardPeriod,
    windowStart: Iso8601,
    windowEnd: Iso8601,
    /** ±2 neighbors around the viewer — micro-board, not the full ranking. */
    entries: z.array(LeaderboardEntry),
    /** The viewer's own row, separately so it can be highlighted. */
    viewerEntry: LeaderboardEntry.optional(),
    computedAt: Iso8601,
});
// ---------------------------------------------------------------------------
// Cognitive metrics (staff-facing only — never resident-facing UI)
// ---------------------------------------------------------------------------
export const CognitiveMetric = z.object({
    residentId: RailsId,
    metricKey: z.string(),
    cognitiveDomain: CognitiveDomain,
    periodStart: Iso8601,
    periodGranularity: z.enum(['week', 'month']),
    value: z.number(),
    baselineValue: z.number().optional(),
    zScore: z.number().optional(),
    trendSlope: z.number().optional(),
    sampleSize: z.number().int().nonnegative(),
    confidence: z.number().min(0).max(1),
});
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
export const ApiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
// ---------------------------------------------------------------------------
// Endpoint map — type-level documentation of the surface.
// ---------------------------------------------------------------------------
/** Path templates; replace `:slug`, `:publicId` etc. at call sites. */
export const Endpoints = {
    catalog: '/community_os/v1/games',
    gameDefinition: '/community_os/v1/games/:slug',
    startSession: '/community_os/v1/games/:slug/sessions',
    endSession: '/community_os/v1/game_sessions/:publicId',
    postEvents: '/community_os/v1/game_sessions/:publicId/events',
    sync: '/community_os/v1/game_sync',
    leaderboard: '/community_os/v1/games/:slug/leaderboard',
    history: '/community_os/v1/residents/me/game_history',
    residentMetrics: '/community_os/v1/residents/:id/cognitive_metrics',
};
//# sourceMappingURL=api.js.map